<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Weekly Labels</h5>
          <small class="text-muted">Manage weekly planner labels by session and date</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ paginationStart }}</strong> to
            <strong>{{ paginationEnd }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="loadWeeklyLabels"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row week-tile-filters mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-3 d-flex align-items-center flex-grow-1">
            <b-form-input
              v-model.trim="searchQuery"
              class="filter-search-input"
              placeholder="Search..."
              @input="onSearchInput"
            />
            <v-select
              v-model="filters.sessionID"
              :options="sessions"
              label="session"
              :reduce="(val) => val.id"
              :clearable="true"
              placeholder="Session"
              class="filter-control filter-field-item week-tile-filter-select planner-filter-select"
              @input="onFilterChange"
            />
            <div class="filter-date-wrap">
              <flat-pickr
                v-model="dateRange"
                :config="rangeDateConfig"
                class="form-control filter-control filter-date-item"
                placeholder="Date range"
                @on-change="onDateRangeChange"
              />
            </div>
          </div>

          <b-button
            class="action-btn-filter filter-clear-btn mx-50"
            v-b-tooltip.hover.top
            title="Clear filters"
            @click="clearFilters"
          >
            <span class="filter-clear-x" aria-hidden="true">×</span>
          </b-button>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="openAddModal"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Weekly Label</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <b-table
          class="planner-list-table mt-1"
          :items="weeklyLabels"
          :fields="fields"
          :busy="dataLoading"
          show-empty
          :responsive="false"
          hover
          small
        >
          <template #empty>
            <h3 class="mt-1 mb-1 text-center">{{ $t("No records found") }}</h3>
          </template>

          <template #table-busy>
            <div class="list-skeleton">
              <div
                v-for="n in 6"
                :key="'skeleton-' + n"
                class="list-skeleton__row"
                :style="{
                  gridTemplateColumns: `repeat(${fields.length}, minmax(0, 1fr))`,
                }"
              >
                <div
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + n + '-' + index"
                  class="list-skeleton__cell"
                >
                  <div
                    class="list-skeleton__bar"
                    :style="{ width: skeletonBarWidth(index) }"
                  ></div>
                </div>
              </div>
            </div>
          </template>

          <template #cell(sno)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

          <template #cell(title)="data">
            <b-badge variant="light-primary">{{ data.value }}</b-badge>
          </template>

          <template #cell(sessionName)="{ item }">
            <b-badge variant="light-info">{{ item.sessionName || "—" }}</b-badge>
          </template>

          <template #cell(dateFrom)="{ item }">
            <b-badge variant="light-secondary">{{ formatDate(item.dateFrom) }}</b-badge>
          </template>

          <template #cell(dateTo)="{ item }">
            <b-badge variant="light-secondary">{{ formatDate(item.dateTo) }}</b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="editWeeklyLabel(item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteWeeklyLabel(item)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
        </b-table>
      </div>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Weekly Label' : 'Add Weekly Label'"
      @hidden="resetForm"
      centered
      no-close-on-backdrop
    >
      <b-form @submit.prevent>
        <b-row>
          <b-col md="12">
            <b-form-group label="Session *">
              <v-select
                v-model="form.sessionID"
                :options="sessions"
                label="session"
                :reduce="(val) => val.id"
                :clearable="false"
                placeholder="Select session"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="12">
            <b-form-group label="Title" label-for="title">
              <b-form-input
                v-model.trim="form.title"
                placeholder="Enter weekly label title"
              />
            </b-form-group>
          </b-col>
        </b-row>
        <b-row>
          <b-col md="6">
            <b-form-group label="Date From" label-for="dateFrom">
              <flat-pickr
                v-model="form.dateFrom"
                :config="dateConfig"
                class="form-control"
                placeholder="Select start date"
              />
            </b-form-group>
          </b-col>
          <b-col md="6">
            <b-form-group label="Date To" label-for="dateTo">
              <flat-pickr
                v-model="form.dateTo"
                :config="dateConfig"
                class="form-control"
                placeholder="Select end date"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false">
          Cancel
        </b-button>
        <b-button
          variant="primary"
          @click="saveWeeklyLabel"
          :disabled="loading"
        >
          <b-spinner v-if="loading" small />
          {{ isEdit ? "Update" : "Save" }}
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BBadge,
  BPagination,
  BModal,
  BForm,
  BFormGroup,
  BSpinner,
  VBTooltip,
} from "bootstrap-vue";
import vSelect from "vue-select";
import flatPickr from "vue-flatpickr-component";
import { mapActions } from "vuex";
import axios from "axios";
import plannerFilterDropdownScrollMixin from "./plannerFilterDropdownScroll";

export default {
  name: "WeekTile",
  mixins: [plannerFilterDropdownScrollMixin],
  components: {
    BCard,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BBadge,
    BPagination,
    BModal,
    BForm,
    BFormGroup,
    BSpinner,
    vSelect,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      rights: {},
      weeklyLabels: [],
      sessions: [],
      searchQuery: "",
      searchTimer: null,
      dateRange: "",
      filters: {
        sessionID: null,
        dtFrom: "",
        dtTo: "",
      },
      showModal: false,
      isEdit: false,
      loading: false,
      dataLoading: false,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      dateConfig: {
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      rangeDateConfig: {
        mode: "range",
        altFormat: "j M, Y",
        altInput: true,
        dateFormat: "Y-m-d",
      },
      fields: [
        { key: "sno", label: "S.No", thClass: "text-center", tdClass: "text-center" },
        { key: "title", label: "Title" },
        { key: "sessionName", label: "Session" },
        { key: "dateFrom", label: "Date From" },
        { key: "dateTo", label: "Date To" },
        { key: "actions", label: "Actions", thClass: "text-center", tdClass: "text-center" },
      ],
      form: {
        id: 0,
        title: "",
        dateFrom: "",
        dateTo: "",
        sessionID: null,
        campusID:
          (this.$store.state.userData && this.$store.state.userData.cId) || 0,
        isActive: true,
      },
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      const campusId =
        (this.$store.state.userData && this.$store.state.userData.cId) || 0;
      return this.reportDomain === "myskooliuss" ? 1 : campusId;
    },
    paginationStart() {
      const total = Number(this.totalCount) || 0;
      if (total <= 0) return 0;
      const page = Number(this.currentPage) || 1;
      const perPage = Number(this.perPage) || 20;
      return (page - 1) * perPage + 1;
    },
    paginationEnd() {
      const total = Number(this.totalCount) || 0;
      if (total <= 0) return 0;
      const page = Number(this.currentPage) || 1;
      const perPage = Number(this.perPage) || 20;
      return Math.min(page * perPage, total);
    },
  },
  created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.loadSessions();
    this.loadWeeklyLabels();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    skeletonBarWidth(index) {
      const widths = ["42%", "72%", "58%", "65%", "80%", "70%", "48%", "40%"];
      return widths[index % widths.length];
    },
    formatDate(dateString) {
      if (!dateString) return "-";
      const date = new Date(dateString);
      return date.toLocaleDateString("en-UK", {
        year: "numeric",
        day: "numeric",
        month: "short",
      });
    },

    formatDateForInput(date) {
      const d = new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, "0");
      const day = String(d.getDate()).padStart(2, "0");
      return `${y}-${m}-${day}`;
    },

    buildListUrl() {
      const params = new URLSearchParams({
        pageNumber: String(this.currentPage),
        pageSize: String(this.perPage),
      });

      if (this.searchQuery.trim()) {
        params.set("search", this.searchQuery.trim());
      }
      if (this.filters.dtFrom) {
        params.set("dtFrom", this.filters.dtFrom);
      }
      if (this.filters.dtTo) {
        params.set("dtTo", this.filters.dtTo);
      }
      if (this.filters.sessionID) {
        params.set("sessionID", String(this.filters.sessionID));
      }

      return (
        this.$store.state.domain +
        "PlannerWeeklyLabels/GetByPagination/" +
        this.plannerCampusID +
        "/pagination?" +
        params.toString()
      );
    },

    onSearchInput() {
      clearTimeout(this.searchTimer);
      this.searchTimer = setTimeout(() => {
        this.currentPage = 1;
        this.loadWeeklyLabels();
      }, 400);
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadWeeklyLabels();
    },

    onDateRangeChange(selectedDates) {
      if (selectedDates && selectedDates.length === 2) {
        this.filters.dtFrom = this.formatDateForInput(selectedDates[0]);
        this.filters.dtTo = this.formatDateForInput(selectedDates[1]);
        this.onFilterChange();
      } else if (!selectedDates || selectedDates.length === 0) {
        if (this.filters.dtFrom || this.filters.dtTo) {
          this.filters.dtFrom = "";
          this.filters.dtTo = "";
          this.onFilterChange();
        }
      }
    },

    clearFilters() {
      this.searchQuery = "";
      this.filters.sessionID = null;
      this.filters.dtFrom = "";
      this.filters.dtTo = "";
      this.dateRange = "";
      this.currentPage = 1;
      this.loadWeeklyLabels();
    },

    async loadSessions() {
      try {
        const url = `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${this.plannerCampusID}`;
        const res = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${this.$store.state.userData.token}`,
          },
        });

        if (res.data?.status === "success" && Array.isArray(res.data.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data?.data)) {
          this.sessions = res.data.data;
        } else if (Array.isArray(res.data)) {
          this.sessions = res.data;
        } else {
          this.sessions = [];
        }
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      }
    },

    getDefaultSessionID() {
      if (!this.sessions.length) return null;
      const currentSession = this.sessions.find(
        (el) => el.isCurrentSession === true
      );
      return currentSession
        ? currentSession.id
        : this.sessions[this.sessions.length - 1].id;
    },

    async loadWeeklyLabels() {
      this.dataLoading = true;
      try {
        const config = {
          method: "get",
          url: this.buildListUrl(),
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
            db: this.$store.state.userData.db,
            "Content-Type": "application/json",
          },
        };

        const response = await axios(config);

        if (response.data && Array.isArray(response.data.data)) {
          this.weeklyLabels = response.data.data;
          this.totalCount =
            response.data.total ||
            response.data.count ||
            response.data.data.length;
        } else {
          this.weeklyLabels = [];
          this.totalCount = 0;
        }
      } catch (error) {
        console.error("Error loading weekly labels:", error);
        this.$bvToast.toast("Error loading weekly labels!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        this.weeklyLabels = [];
        this.totalCount = 0;
      }
      this.dataLoading = false;
    },

    openAddModal() {
      this.isEdit = false;
      this.form.sessionID = this.getDefaultSessionID();
      this.form.campusID = this.plannerCampusID;
      this.showModal = true;
    },

    editWeeklyLabel(item) {
      this.isEdit = true;

      // Fix timezone conversion issue for dates
      const formatDateForInput = (dateString) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
      };

      this.form = {
        id: item.id,
        title: item.title,
        dateFrom: formatDateForInput(item.dateFrom),
        dateTo: formatDateForInput(item.dateTo),
        sessionID: item.sessionID || null,
        campusID: item.campusID,
        isActive: item.isActive,
      };
      console.log("Edit form data:", this.form); // Debug log
      console.log("Original item dates:", {
        dateFrom: item.dateFrom,
        dateTo: item.dateTo,
      }); // Debug log
      this.showModal = true;
    },

    async deleteWeeklyLabel(item) {
      const result = await Swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Yes, delete it!",
      });

      if (result.isConfirmed) {
        try {
          const config = {
            method: "delete",
            url:
              this.$store.state.domain +
              "PlannerWeeklyLabels/Delete/" +
              this.plannerCampusID +
              "/" +
              item.id,
            headers: {
              Authorization: "bearer " + this.$store.state.userData.token,
              db: this.$store.state.userData.db,
              "Content-Type": "application/json",
            },
          };

          const response = await axios(config);

          if (response.data.status === "success") {
            this.$bvToast.toast("Weekly label deleted successfully.", {
              title: "Success!",
              variant: "success",
              solid: true,
            });
            this.loadWeeklyLabels();
          } else {
            this.$bvToast.toast(
              response.data.message || "Error deleting weekly label!",
              {
                title: this.$t("Error!"),
                variant: "danger",
                solid: true,
              }
            );
          }
        } catch (error) {
          console.error("Error deleting weekly label:", error);
          this.$bvToast.toast("Error deleting weekly label!", {
            title: this.$t("Error!"),
            variant: "danger",
            solid: true,
          });
        }
      }
    },

    async saveWeeklyLabel() {
      if (
        !this.form.title.trim() ||
        !this.form.dateFrom ||
        !this.form.dateTo ||
        !this.form.sessionID
      ) {
        this.$bvToast.toast("Please fill all required fields!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      // Validate date range
      if (new Date(this.form.dateFrom) > new Date(this.form.dateTo)) {
        this.$bvToast.toast("End date must be after start date!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
        return;
      }

      this.loading = true;

      try {
        // Format dates to ISO string format without timezone conversion
        const formatDateToISO = (dateString, time) => {
          const date = new Date(dateString + "T" + time);
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, "0");
          const day = String(date.getDate()).padStart(2, "0");
          return `${year}-${month}-${day}T${time}.000Z`;
        };

        const dateFrom = formatDateToISO(this.form.dateFrom, "00:00:00");
        const dateTo = formatDateToISO(this.form.dateTo, "23:59:59");

        const config = {
          method: "post", // Always use POST as shown in curl example
          url:
            this.$store.state.domain +
            "PlannerWeeklyLabels/Save/" +
            (this.isEdit ? this.form.id : this.plannerCampusID),
          headers: {
            Authorization: "bearer " + this.$store.state.userData.token,
            db: this.$store.state.userData.db,
            userID: String(this.$store.state.userData.userID), // Ensure userID is string
            "Content-Type": "application/json",
          },
          data: {
            id: this.form.id,
            title: this.form.title,
            dateFrom: dateFrom,
            dateTo: dateTo,
            sessionID: this.form.sessionID,
            campusID: this.isEdit ? this.form.campusID : this.plannerCampusID,
            isActive: this.form.isActive,
          },
        };

        console.log("Sending data to API:", config.data); // Debug log
        console.log("Is edit mode:", this.isEdit); // Debug log
        console.log("HTTP method:", config.method); // Debug log
        console.log("URL:", config.url); // Debug log
        console.log("Headers being sent:", config.headers); // Debug log for headers
        console.log("DB value:", this.$store.state.userData.db); // Debug log for db
        console.log("UserID value:", this.$store.state.userData.userID); // Debug log for userID

        const response = await axios(config);

        if (response.data.status === "success") {
          this.$bvToast.toast(
            "Weekly label " +
              (this.isEdit ? "updated" : "created") +
              " successfully.",
            {
              title: "Success!",
              variant: "success",
              solid: true,
            }
          );
          this.showModal = false;
          this.loadWeeklyLabels();
        } else {
          this.$bvToast.toast(
            response.data.message || "Error saving weekly label!",
            {
              title: this.$t("Error!"),
              variant: "danger",
              solid: true,
            }
          );
        }
      } catch (error) {
        console.error("Error saving weekly label:", error);
        this.$bvToast.toast("Error saving weekly label!", {
          title: this.$t("Error!"),
          variant: "danger",
          solid: true,
        });
      }
      this.loading = false;
    },

    resetForm() {
      this.form = {
        id: 0,
        title: "",
        dateFrom: "",
        dateTo: "",
        sessionID: this.getDefaultSessionID(),
        campusID: this.plannerCampusID,
        isActive: true,
      };
    },

    onPageSizeChange(value) {
      this.perPage = Number(value) || 20;
      this.currentPage = 1;
      this.loadWeeklyLabels();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./plannerListTable.scss";

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/vue-flatpicker.scss";

@import "./plannerFilterDropdownMenu.scss";
</style>
